import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Icon } from './Icon';

export interface ModuleTab {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]['name'];
  count?: number;
  countTone?: 'danger';
  countDanger?: number;
  countDangerTitle?: string;
  children?: Array<{ to: string; label: string; count?: number }>;
}

function matchesRoute(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function matchesDestination(pathname: string, search: string, to: string) {
  const [targetPath, targetSearch = ''] = to.split('?');
  if (!matchesRoute(pathname, targetPath)) return false;
  const currentParams = new URLSearchParams(search);
  return [...new URLSearchParams(targetSearch)].every(([key, value]) =>
    currentParams.getAll(key).includes(value));
}

/** CSS controls viewport visibility, including narrow windows with a mouse.
 * The panel stays outside the desktop navigation's scroll rail.
 */
export function MobileModuleNavigation({ tabs }: { tabs: ModuleTab[] }) {
  const { pathname, search } = useLocation();
  const current = tabs.find(tab => matchesRoute(pathname, tab.to) ||
    tab.children?.some(child => matchesRoute(pathname, child.to)));
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const rootRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const toggle = () => {
    if (!open) setExpanded(current?.children ? [current.to] : []);
    setOpen(!open);
  };

  return (
    <nav ref={rootRef} className="module-mobile-navigation" aria-label="Navegación del inventario">
      <button ref={triggerRef} type="button" className="module-mobile-menu-trigger"
        aria-expanded={open} aria-controls={panelId} onClick={toggle}>
        <span className="module-mobile-menu-current">Menú: {current?.label ?? 'Inventario'}</span>
        <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div id={panelId} className="module-mobile-menu-panel">
          <ul className="module-mobile-menu-list">
            {tabs.map((tab, index) => {
              const childrenId = `${panelId}-children-${index}`;
              const childrenOpen = expanded.includes(tab.to);
              return (
                <li key={tab.to}>
                  <div className="module-mobile-menu-row">
                    <NavLink to={tab.to} className={`module-mobile-menu-link${current === tab ? ' active' : ''}`}
                      onClick={() => setOpen(false)}>
                      <Icon name={tab.icon} />
                      <span>{tab.label}</span>
                      {typeof tab.count === 'number' && tab.count > 0 && (
                        <span className={`count${tab.countTone === 'danger' ? ' badge-danger' : ''}`}>{tab.count}</span>
                      )}
                      {typeof tab.countDanger === 'number' && tab.countDanger > 0 && (
                        <span className="count badge-danger" title={tab.countDangerTitle}>{tab.countDanger}</span>
                      )}
                    </NavLink>
                    {tab.children && (
                      <button type="button" className="module-mobile-menu-expand"
                        aria-label={`Secciones de ${tab.label}`} aria-expanded={childrenOpen} aria-controls={childrenId}
                        onClick={() => setExpanded(previous => previous.includes(tab.to)
                          ? previous.filter(to => to !== tab.to) : [...previous, tab.to])}>
                        <span aria-hidden="true">{childrenOpen ? '▴' : '▾'}</span>
                      </button>
                    )}
                  </div>
                  {tab.children && childrenOpen && (
                    <ul id={childrenId} className="module-mobile-menu-children">
                      {tab.children.map(child => {
                        const active = matchesDestination(pathname, search, child.to);
                        return (
                        <li key={child.to}>
                          <Link to={child.to}
                            aria-current={active ? 'page' : undefined}
                            className={`module-mobile-menu-link${active ? ' active' : ''}`}
                            onClick={() => setOpen(false)}>
                            <span>{child.label}</span>
                            {typeof child.count === 'number' && child.count > 0 && <span className="count">{child.count}</span>}
                          </Link>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </nav>
  );
}
